import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Minus, Plus, MessageCircle, Share2, Smartphone } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { formatRupiah, getPrimaryImage, stockInfo, buildWhatsAppText, buildWhatsAppUrl, productUrl, absUrl } from "@/lib/helpers";
import ProductCard from "@/components/ProductCard";

export default function ProductDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { settings } = useStore();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [modelId, setModelId] = useState("");
  const [qty, setQty] = useState(1);

  useEffect(() => {
    setLoading(true);
    setActiveImg(0);
    setModelId("");
    setQty(1);
    api.get(`/products/${slug}`)
      .then((r) => setProduct(r.data))
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) return <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10"><Skeleton className="h-96 w-full rounded-2xl" /></div>;
  if (!product) return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-20 text-center">
      <p className="font-heading text-xl font-semibold">Produk tidak ditemukan</p>
      <Link to="/katalog" className="mt-4 inline-block text-accent hover:underline">Kembali ke katalog</Link>
    </div>
  );

  const stock = stockInfo(product.stock_status);
  const soldOut = product.stock_status === "habis";
  const hasPromo = product.promo_price && product.promo_price < product.price;
  const unit = hasPromo ? product.promo_price : product.price;
  const total = unit * qty;
  const images = product.images?.length ? product.images : [{ url: getPrimaryImage(product), alt: product.name }];
  const models = product.compatible_models || [];
  const selectedModel = models.find((m) => m.id === modelId);

  const handleOrder = () => {
    if (models.length > 0 && !modelId) {
      toast.error("Pilih tipe HP terlebih dahulu");
      return;
    }
    const ctx = {
      store_name: settings?.store_name,
      nama_produk: product.name,
      kategori: product.category_name || "-",
      tipe_hp: selectedModel?.name || (models[0]?.name ?? "-"),
      jumlah: qty,
      harga: formatRupiah(unit),
      total: formatRupiah(total),
      url_gambar: getPrimaryImage(product),
      url_produk: productUrl(product.slug),
    };
    const text = buildWhatsAppText(settings?.whatsapp_template, ctx);
    api.post("/track/whatsapp-click", { product_id: product.id, model_id: modelId || null }).catch(() => {});
    window.open(buildWhatsAppUrl(settings?.whatsapp_number, text), "_blank");
  };

  const share = async () => {
    const url = productUrl(product.slug);
    try {
      if (navigator.share) await navigator.share({ title: product.name, url });
      else { await navigator.clipboard.writeText(url); toast.success("Link produk disalin"); }
    } catch (e) { /* cancelled */ }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-6">
      <button onClick={() => navigate(-1)} className="mb-4 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ChevronLeft className="h-4 w-4" /> Kembali
      </button>

      <div className="grid gap-8 lg:grid-cols-2">
        {/* GALLERY */}
        <div>
          <div className="overflow-hidden rounded-3xl border border-border bg-secondary">
            <img data-testid="product-detail-image" src={absUrl(images[activeImg].url)} alt={images[activeImg].alt || product.name} className="aspect-square w-full object-cover" />
          </div>
          {images.length > 1 && (
            <div className="mt-3 flex gap-2 overflow-x-auto no-scrollbar">
              {images.map((im, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={`h-16 w-16 shrink-0 overflow-hidden rounded-xl border-2 transition-colors ${i === activeImg ? "border-accent" : "border-transparent"}`}>
                  <img src={absUrl(im.url)} alt={im.alt || `foto ${i + 1}`} className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* INFO */}
        <div>
          <div className="flex items-start justify-between gap-3">
            <div>
              {product.category_name && <span className="text-xs font-semibold uppercase tracking-wider text-accent">{product.category_name}</span>}
              <h1 className="mt-1 font-heading text-2xl sm:text-3xl font-bold tracking-tight">{product.name}</h1>
              <p className="mt-1 text-xs text-muted-foreground">Kode: {product.code}</p>
            </div>
            <button onClick={share} data-testid="share-btn" className="rounded-xl border border-border p-2.5 hover:bg-secondary" aria-label="Bagikan"><Share2 className="h-4 w-4" /></button>
          </div>

          <div className="mt-4 flex items-center gap-3">
            <span className="font-heading text-2xl sm:text-3xl font-extrabold">{formatRupiah(unit)}</span>
            {hasPromo && <span className="text-base text-muted-foreground line-through">{formatRupiah(product.price)}</span>}
            <span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${stock.cls}`}>{stock.label}</span>
          </div>

          {product.description && <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{product.description}</p>}

          <div className="mt-5 space-y-3 rounded-2xl border border-border bg-card p-4">
            <label className="flex items-center gap-1.5 text-sm font-semibold"><Smartphone className="h-4 w-4 text-accent" /> Pilih Tipe HP {models.length > 0 && <span className="text-rose-500">*</span>}</label>
            {models.length > 0 ? (
              <Select value={modelId} onValueChange={setModelId}>
                <SelectTrigger data-testid="detail-model-select"><SelectValue placeholder="Pilih tipe HP kompatibel" /></SelectTrigger>
                <SelectContent>
                  {models.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-sm text-muted-foreground">Universal / semua tipe HP.</p>
            )}

            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold">Jumlah</span>
              <div className="flex items-center gap-2">
                <button data-testid="qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="flex h-9 w-9 items-center justify-center rounded-lg border border-border hover:bg-secondary"><Minus className="h-4 w-4" /></button>
                <span data-testid="qty-value" className="w-8 text-center font-semibold">{qty}</span>
                <button data-testid="qty-plus" onClick={() => setQty((q) => q + 1)} className="flex h-9 w-9 items-center justify-center rounded-lg border border-border hover:bg-secondary"><Plus className="h-4 w-4" /></button>
              </div>
            </div>
            <div className="flex items-center justify-between border-t border-border pt-3">
              <span className="text-sm text-muted-foreground">Perkiraan Total</span>
              <span data-testid="detail-total" className="font-heading text-lg font-bold">{formatRupiah(total)}</span>
            </div>
          </div>

          <button
            data-testid="btn-whatsapp-order"
            onClick={handleOrder}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl bg-whatsapp py-4 text-base font-bold text-white shadow-lg shadow-emerald-500/20 transition-transform hover:scale-[1.02] active:scale-95"
          >
            <MessageCircle className="h-5 w-5" /> {soldOut ? "Tanya Ketersediaan via WhatsApp" : "Pesan Sekarang via WhatsApp"}
          </button>
          {settings?.shipping_note && <p className="mt-2 text-center text-xs text-muted-foreground">{settings.shipping_note}</p>}
        </div>
      </div>

      {/* RELATED */}
      {product.related?.length > 0 && (
        <section className="mt-14">
          <h2 className="font-heading text-xl sm:text-2xl font-bold tracking-tight">Produk Terkait</h2>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 sm:gap-6">
            {product.related.map((r) => <ProductCard key={r.id} product={r} />)}
          </div>
        </section>
      )}
    </div>
  );
}
